import * as Dialog from '@radix-ui/react-dialog'
import * as Popover from '@radix-ui/react-popover'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useIsCompact } from '@/lib/use-media-query'
import { cn } from '@/lib/utils'

interface PopoverSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Krijgt `asChild`, dus een enkel element dat een ref en handlers accepteert. */
  trigger: ReactNode
  /** Voor de schermlezer; op telefoonformaat staat hij ook zichtbaar boven het paneel. */
  title: string
  children: ReactNode
  align?: 'start' | 'end'
  /** Breedte van de popover; de bottom sheet vult altijd het scherm. */
  className?: string
}

/**
 * Een keuzepaneel dat zich naar de schermmaat voegt: een popover naast zijn knop op
 * desktop, en onder `sm` een bottom sheet over de volle breedte. Een popover van 16rem
 * op een telefoon laat van een cascade te weinig over om in te lezen, en een modaal
 * venster naast een knop op een breed scherm is te veel ceremonie voor een keuze.
 *
 * Beide takken zijn Radix, dus focusval, Escape en klik-buiten gedragen zich hetzelfde.
 */
export function PopoverSheet({ open, onOpenChange, trigger, title, children, align = 'start', className }: PopoverSheetProps) {
  const compact = useIsCompact()

  if (compact) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-(--scrim) backdrop-blur-xs" />
          <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col overflow-hidden rounded-t-card bg-surface shadow-pop animate-fade-in focus:outline-hidden">
            <div className="flex items-center justify-between gap-4 border-b border-hairline px-4 py-3">
              <Dialog.Title className="text-base font-semibold text-ink">{title}</Dialog.Title>
              <Dialog.Close className="btn-ghost h-8 w-8 shrink-0 p-0" aria-label="Sluiten">
                <X className="h-4 w-4" />
              </Dialog.Close>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">{children}</div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={6}
          aria-label={title}
          className={cn(
            'z-40 flex flex-col overflow-hidden rounded-card border border-hairline bg-surface shadow-pop animate-fade-in',
            className ?? 'w-80',
          )}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
